import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemSecondaryAction,
	ListItemText,
	makeStyles,
	MenuItem,
	TextField,
	Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Close } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { formatDue, toLocalInput } from "./format";

const useStyles = makeStyles(theme => ({
	form: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.5),
	},
	section: {
		marginTop: theme.spacing(2),
		fontWeight: 600,
	},
	taskRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
	},
	grow: { flex: "1 1 180px" },
	done: { textDecoration: "line-through", color: theme.palette.text.secondary },
}));

const defaultDue = () => toLocalInput(new Date(Date.now() + 24 * 60 * 60 * 1000));

/** Creates (no `opportunity`) or edits an opportunity, with its tasks. */
const OpportunityDialog = ({ open, onClose, onChanged, opportunity, stages, users, isAdmin }) => {
	const classes = useStyles();
	const history = useHistory();
	const editing = Boolean(opportunity);

	const [title, setTitle] = useState("");
	const [value, setValue] = useState("");
	const [notes, setNotes] = useState("");
	const [stageId, setStageId] = useState("");
	const [ownerId, setOwnerId] = useState("");
	const [contact, setContact] = useState(null);
	const [contactOptions, setContactOptions] = useState([]);
	const [contactInput, setContactInput] = useState("");
	const [saving, setSaving] = useState(false);

	const [tasks, setTasks] = useState([]);
	const [taskTitle, setTaskTitle] = useState("");
	const [taskDue, setTaskDue] = useState(defaultDue());

	useEffect(() => {
		if (!open) return;
		setTitle(opportunity?.title || "");
		setValue(opportunity ? String(opportunity.value).replace(".", ",") : "");
		setNotes(opportunity?.notes || "");
		setStageId(opportunity?.stageId || "");
		setOwnerId(opportunity?.userId || "");
		setContact(null);
		setContactInput("");
		setTaskTitle("");
		setTaskDue(defaultDue());
	}, [open, opportunity]);

	useEffect(() => {
		if (!open || editing || contactInput.length < 3) return undefined;
		const timer = setTimeout(async () => {
			try {
				const { data } = await api.get("contacts", { params: { searchParam: contactInput } });
				setContactOptions(data.contacts || []);
			} catch (err) {
				toastError(err);
			}
		}, 400);
		return () => clearTimeout(timer);
	}, [contactInput, open, editing]);

	const loadTasks = useCallback(async () => {
		if (!opportunity) return;
		try {
			const { data } = await api.get(`/funnel/opportunities/${opportunity.id}/tasks`);
			setTasks(data.tasks || []);
		} catch (err) {
			toastError(err);
		}
	}, [opportunity]);

	useEffect(() => {
		if (open && opportunity) void loadTasks();
		else setTasks([]);
	}, [open, opportunity, loadTasks]);

	const save = async () => {
		if (!editing && !contact) {
			toast.warn(i18n.t("funnel.dialog.pickContact"));
			return;
		}
		setSaving(true);
		try {
			const body = { title, value, notes };
			if (isAdmin && ownerId) body.userId = ownerId;
			if (editing) {
				await api.put(`/funnel/opportunities/${opportunity.id}`, body);
			} else {
				await api.post("/funnel/opportunities", {
					...body,
					contactId: contact.id,
					stageId: stageId || undefined,
				});
			}
			toast.success(i18n.t("funnel.dialog.saved"));
			onChanged();
			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const addTask = async () => {
		if (!taskTitle.trim()) return;
		try {
			await api.post(`/funnel/opportunities/${opportunity.id}/tasks`, {
				title: taskTitle,
				dueAt: taskDue ? new Date(taskDue).toISOString() : null,
			});
			setTaskTitle("");
			setTaskDue(defaultDue());
			await loadTasks();
			onChanged();
		} catch (err) {
			toastError(err);
		}
	};

	const toggleTask = async task => {
		try {
			await api.put(`/funnel/tasks/${task.id}`, { done: !task.doneAt });
			await loadTasks();
			onChanged();
		} catch (err) {
			toastError(err);
		}
	};

	const removeTask = async task => {
		try {
			await api.delete(`/funnel/tasks/${task.id}`);
			await loadTasks();
			onChanged();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				{editing ? i18n.t("funnel.dialog.editTitle") : i18n.t("funnel.dialog.newTitle")}
			</DialogTitle>
			<DialogContent dividers>
				<div className={classes.form}>
					<TextField
						id="opp-title"
						label={i18n.t("funnel.dialog.title")}
						value={title}
						onChange={e => setTitle(e.target.value)}
						inputProps={{ maxLength: 120 }}
						variant="outlined"
						size="small"
						autoFocus
					/>
					{editing ? (
						<Typography variant="body2">
							{i18n.t("funnel.dialog.contact")}: <b>{opportunity.contactName}</b>
							{opportunity.contactNumber ? ` · ${opportunity.contactNumber}` : ""}
							{opportunity.ticketId ? (
								<Button
									size="small"
									onClick={() => history.push(`/tickets/${opportunity.ticketId}`)}
								>
									{i18n.t("funnel.dialog.openTicket")}
								</Button>
							) : null}
						</Typography>
					) : (
						<Autocomplete
							options={contactOptions}
							getOptionLabel={o => `${o.name} · ${o.number}`}
							getOptionSelected={(o, v) => o.id === v.id}
							filterOptions={x => x}
							value={contact}
							onChange={(_, v) => setContact(v)}
							onInputChange={(_, v) => setContactInput(v)}
							noOptionsText={i18n.t("funnel.dialog.contactHint")}
							renderInput={params => (
								<TextField
									{...params}
									label={i18n.t("funnel.dialog.contact")}
									variant="outlined"
									size="small"
								/>
							)}
						/>
					)}
					<TextField
						id="opp-value"
						label={i18n.t("funnel.dialog.value")}
						value={value}
						onChange={e => setValue(e.target.value)}
						variant="outlined"
						size="small"
						placeholder="0,00"
						inputProps={{ inputMode: "decimal" }}
					/>
					{!editing && (
						<TextField
							select
							id="opp-stage"
						label={i18n.t("funnel.dialog.stage")}
							value={stageId}
							onChange={e => setStageId(e.target.value)}
							variant="outlined"
							size="small"
						>
							<MenuItem value="">{i18n.t("funnel.dialog.firstStage")}</MenuItem>
							{stages.map(s => (
								<MenuItem key={s.id} value={s.id}>
									{s.name}
								</MenuItem>
							))}
						</TextField>
					)}
					{isAdmin && (
						<TextField
							select
							id="opp-owner"
						label={i18n.t("funnel.dialog.owner")}
							value={ownerId}
							onChange={e => setOwnerId(e.target.value)}
							variant="outlined"
							size="small"
						>
							{!editing && <MenuItem value="">{i18n.t("funnel.dialog.me")}</MenuItem>}
							{users.map(u => (
								<MenuItem key={u.id} value={u.id}>
									{u.name}
								</MenuItem>
							))}
						</TextField>
					)}
					<TextField
						id="opp-notes"
						label={i18n.t("funnel.dialog.notes")}
						value={notes}
						onChange={e => setNotes(e.target.value)}
						inputProps={{ maxLength: 2000 }}
						variant="outlined"
						size="small"
						multiline
						rows={3}
					/>
				</div>

				{editing && (
					<>
						<Typography className={classes.section}>{i18n.t("funnel.tasks.title")}</Typography>
						{tasks.length === 0 ? (
							<Typography variant="body2" color="textSecondary">
								{i18n.t("funnel.tasks.empty")}
							</Typography>
						) : (
							<List dense disablePadding>
								{tasks.map(task => (
									<ListItem key={task.id} dense disableGutters>
										<ListItemIcon style={{ minWidth: 36 }}>
											<Checkbox
												edge="start"
												checked={Boolean(task.doneAt)}
												onChange={() => toggleTask(task)}
											/>
										</ListItemIcon>
										<ListItemText
											primary={task.title}
											primaryTypographyProps={{ className: task.doneAt ? classes.done : undefined }}
											secondary={
												<>
													{task.dueAt ? formatDue(task.dueAt) : i18n.t("funnel.tasks.noDue")}
													{task.userName ? ` · ${task.userName}` : ""}
													{task.overdue && (
														<Chip
															size="small"
															color="secondary"
															label={i18n.t("funnel.tasks.overdue")}
															style={{ marginLeft: 8 }}
														/>
													)}
												</>
											}
										/>
										<ListItemSecondaryAction>
											<IconButton
												size="small"
												aria-label={i18n.t("funnel.tasks.remove")}
												onClick={() => removeTask(task)}
											>
												<Close fontSize="small" />
											</IconButton>
										</ListItemSecondaryAction>
									</ListItem>
								))}
							</List>
						)}
						<div className={classes.taskRow}>
							<TextField
								className={classes.grow}
								id="task-title"
						label={i18n.t("funnel.tasks.new")}
								value={taskTitle}
								onChange={e => setTaskTitle(e.target.value)}
								inputProps={{ maxLength: 160 }}
								variant="outlined"
								size="small"
							/>
							<TextField
								type="datetime-local"
								id="task-due"
						label={i18n.t("funnel.tasks.due")}
								value={taskDue}
								onChange={e => setTaskDue(e.target.value)}
								InputLabelProps={{ shrink: true }}
								variant="outlined"
								size="small"
							/>
							<Button variant="outlined" color="primary" onClick={addTask} disabled={!taskTitle.trim()}>
								{i18n.t("funnel.tasks.add")}
							</Button>
						</div>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					{i18n.t("funnel.dialog.cancel")}
				</Button>
				<Button
					onClick={save}
					color="primary"
					variant="contained"
					disabled={saving || !title.trim()}
				>
					{saving ? <CircularProgress size={20} /> : i18n.t("funnel.dialog.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default OpportunityDialog;
